/**
 * ============================================================================
 *  File        : frontend/src/pages/Workbenches.tsx
 *  Module      : pages
 *  Purpose     : React resource pages — 案件列表、使用者/角色、主檔、報表、稽核
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import type { ReactElement } from 'react'
import { ResourceWorkbench } from '../components/ResourceWorkbench'
import { modules } from '../data/navigation'
import type { ResourceConfig } from '../types'

interface PageProps {
  t: (key: string) => string
}

const usersResource: ResourceConfig = {
  id: 'admin-users',
  titleKey: 'admin.users',
  descriptionKey: 'resource.adminUsers.description',
  listPath: '/api/v1/admin/users',
  createPath: '/api/v1/admin/users',
  updatePath: '/api/v1/admin/users/:id',
  idKey: 'id',
  primaryActionKey: 'common.create',
  emptyKey: 'common.empty',
  columns: ['id', 'name', 'email', 'primary_organization_id', 'locale', 'is_active'],
  fields: [
    { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
    { key: 'email', labelKey: 'field.email', kind: 'text', required: true },
    { key: 'password', labelKey: 'field.password', kind: 'text' },
    { key: 'primary_organization_id', labelKey: 'field.primaryOrganizationId', kind: 'number' },
    { key: 'locale', labelKey: 'field.locale', kind: 'text' },
    { key: 'is_active', labelKey: 'field.status', kind: 'checkbox' },
  ],
}

const rolesResource: ResourceConfig = {
  id: 'admin-roles',
  titleKey: 'admin.roles',
  descriptionKey: 'resource.adminRoles.description',
  listPath: '/api/v1/admin/roles',
  createPath: '/api/v1/admin/roles',
  updatePath: '/api/v1/admin/roles/:id',
  idKey: 'id',
  primaryActionKey: 'common.create',
  emptyKey: 'common.empty',
  columns: ['id', 'name', 'display_name', 'is_system'],
  fields: [
    { key: 'name', labelKey: 'field.code', kind: 'text', required: true },
    { key: 'display_name', labelKey: 'field.displayName', kind: 'text', required: true },
    { key: 'is_system', labelKey: 'field.isSystem', kind: 'checkbox' },
  ],
}

const auditResource: ResourceConfig = {
  id: 'audit',
  titleKey: 'nav.audit',
  descriptionKey: 'resource.audit.description',
  listPath: '/api/v1/admin/audit-trails',
  idKey: 'id',
  primaryActionKey: 'common.create',
  emptyKey: 'common.empty',
  columns: ['id', 'auditable_type', 'auditable_id', 'event_name', 'created_at'],
  fields: [],
}

export function AdminUsersPage({ t }: PageProps): ReactElement {
  return (
    <section className="page-stack">
      <PageIntro moduleId="admin-users" t={t} />
      <ResourceWorkbench config={usersResource} t={t} />
    </section>
  )
}

export function AdminRolesPage({ t }: PageProps): ReactElement {
  return (
    <section className="page-stack">
      <PageIntro moduleId="admin-roles" t={t} />
      <ResourceWorkbench config={rolesResource} t={t} />
    </section>
  )
}

export function ReportsPage({ t }: PageProps): ReactElement {
  return (
    <section className="page-stack">
      <PageIntro moduleId="reports" t={t} />
      <div className="module-grid compact">
        {[
          'report.progress',
          'report.weight',
          'report.settlement',
          'report.auction',
          'report.freee',
          'report.audit',
        ].map((key) => (
          <article className="module-card" key={key}>
            <div>
              <span className="status-pill partial">{t('status.partial')}</span>
              <h2>{t(key)}</h2>
              <p>{t('resource.reports.description')}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

export function AuditPage({ t }: PageProps): ReactElement {
  return (
    <section className="page-stack">
      <PageIntro moduleId="audit" t={t} />
      <ResourceWorkbench config={auditResource} t={t} />
    </section>
  )
}

function PageIntro({
  moduleId,
  t,
}: {
  moduleId: string
  t: (key: string) => string
}): ReactElement {
  const module = modules.find((item) => item.id === moduleId)
  return (
    <header className="page-heading">
      <div>
        <span className="eyebrow">{module ? t(`status.${module.status}`) : t('app.name')}</span>
        <h1>{module ? t(module.labelKey) : t('app.name')}</h1>
        <p>{module ? t(module.descriptionKey) : t('desc.dashboard')}</p>
      </div>
    </header>
  )
}
